import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const FAQS = [
    {
        q: "Pourquoi ne pas utiliser de ciment sur un mur ancien ?",
        a: "Les murs anciens (pierre, terre, pisé, brique de pays) ont besoin de respirer pour évacuer naturellement l'humidité. Le ciment et les enduits modernes étanches piègent l'eau dans le mur, provoquent salpêtre, décollements et dégradations accélérées. La chaux et les enduits biosourcés laissent au mur sa perspirance et préservent son intégrité.",
    },
    {
        q: "Combien coûte un diagnostic du bâti ancien ?",
        a: "Un diagnostic complet inclut une visite de site, une analyse des désordres (humidité, fissures, ventilation, structure) et un rapport écrit avec préconisations. Le tarif dépend de la surface et de la complexité — comptez à partir de 380 €. Je vous établis un devis précis après un premier échange téléphonique gratuit.",
    },
    {
        q: "Qu'est-ce qu'un matériau biosourcé ?",
        a: "Un matériau biosourcé est issu de la biomasse végétale ou animale : chaux (minéral), terre crue, chanvre, lin, paille, bois, liège, ouate de cellulose. Ces matériaux sont sains, recyclables, à faible impact carbone et compatibles avec les murs anciens car ils régulent naturellement l'hygrométrie.",
    },
    {
        q: "Dans quelle zone géographique intervenez-vous ?",
        a: "Mon atelier est basé à Saint-Lubin-des-Joncherets (28350) dans l'Eure-et-Loir. J'interviens en Eure-et-Loir, Eure, Yvelines, Île-de-France, Orne, Perche et régions limitrophes. Pour les missions de diagnostic ponctuelles, des déplacements plus lointains sont possibles, n'hésitez pas à me consulter.",
    },
    {
        q: "Proposez-vous des formations à domicile ou sur chantier ?",
        a: "Oui. En plus des stages catalogue (chaux, terre, diagnostic, isolation biosourcée), je conçois des formations sur-mesure adaptées à votre projet : sur votre propre maison en rénovation, dans votre atelier, ou pour une équipe d'artisans. Le format peut être présentiel, sur chantier-école ou hybride.",
    },
    {
        q: "Faites-vous l'accompagnement complet d'un projet de rénovation ?",
        a: "Oui. En tant que diagnostiqueur indépendant, je peux vous accompagner du début à la fin : diagnostic initial, rédaction du cahier des charges, sélection des artisans, contrôle des devis, suivi de chantier et réception. Vous gardez la main sur votre projet, je sécurise les choix techniques.",
    },
];

export default function FAQ() {
    const ref = useReveal();
    const [open, setOpen] = useState(0);

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <section
            id="faq"
            ref={ref}
            data-testid="faq-section"
            className="relative py-24 md:py-32 bg-sand/20 border-y border-border/50"
        >
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
            <div className="max-w-5xl mx-auto px-6 md:px-10">
                <div className="reveal mb-14 md:mb-16">
                    <span className="label-overline text-brand">Questions fréquentes</span>
                    <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                        Vos questions, <span className="italic">mes réponses.</span>
                    </h2>
                </div>

                <div className="divide-y divide-border border-y border-border bg-card rounded-sm">
                    {FAQS.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <article key={i} data-testid={`faq-item-${i}`} className="reveal">
                                <button
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    data-testid={`faq-toggle-${i}`}
                                    className="w-full flex items-center justify-between gap-6 py-6 px-6 md:px-8 text-left group"
                                >
                                    <h3 className="font-heading text-lg md:text-xl font-medium tracking-tight text-ink group-hover:text-brand transition-colors">
                                        {f.q}
                                    </h3>
                                    <span className="shrink-0 w-9 h-9 rounded-sm border border-border flex items-center justify-center text-brand">
                                        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                                    </span>
                                </button>
                                <div
                                    className={`grid transition-all duration-500 ease-out ${
                                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                    }`}
                                >
                                    <div className="overflow-hidden">
                                        <p className="px-6 md:px-8 pb-7 text-stone2 leading-relaxed text-base max-w-3xl">
                                            {f.a}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
